/**
 * P419.1 -- composing a composite scenario.
 *
 * P418 built composition because SAMA's ninth mandated scenario is one
 * scenario carrying three concurrent components on different targets
 * with different durations. It shipped with no way to author one, so a
 * composite was something you made with raw SQL.
 *
 * Two things this surface has to say out loud, because both are
 * consequences of the act rather than separate decisions the operator
 * takes. Joining a composite REMOVES a scenario from the library --
 * SAMA sent nine, and a register showing eleven because three of them
 * are components of the ninth is a register a bank cannot put in front
 * of an examiner. And an offset is measured from the COMPOSITE's
 * onset, not the member's own.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useToast } from "shared/toast/ToastProvider";
import type { ScenarioSummary } from "shared/api/types";
import {
  useAddMember,
  useRemoveMember,
  useScenarioMembers,
} from "twin/testruns/testRunHooks";

export function ScenarioMembersSection({
  scenarioId,
  scenarios,
  isAdmin,
}: {
  scenarioId: string;
  scenarios: ScenarioSummary[];
  isAdmin: boolean;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const members = useScenarioMembers(scenarioId);
  const add = useAddMember(scenarioId);
  const remove = useRemoveMember(scenarioId);
  const [memberId, setMemberId] = useState("");
  const [offset, setOffset] = useState("0");

  const rows = Array.isArray(members.data) ? members.data : [];
  const held = new Set(rows.map((r) => r.member_scenario_id));
  // A scenario cannot be its own member, and one already held is not a
  // candidate -- the picker offering either is how the P419.1 backend
  // ends up returning a 422 or a 409 for something the form could have
  // refused to ask.
  const candidates = scenarios.filter((s) => s.id !== scenarioId && !held.has(s.id));

  return (
    <section
      data-testid="scenario-members"
      className="rounded border border-slate-200 bg-white p-3"
    >
      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        {t("screens.scenariosView.members.heading")}
      </h3>
      {rows.length === 0 ? (
        <p data-testid="scenario-members-empty" className="mt-1 text-xs text-slate-500">
          {t("screens.scenariosView.members.none")}
        </p>
      ) : (
        <ul className="mt-2 space-y-1">
          {rows.map((m) => (
            <li
              key={m.id}
              data-testid="scenario-member-row"
              className="flex items-baseline justify-between text-xs"
            >
              <span className="text-slate-700">
                {m.member_name}{" "}
                <span className="text-slate-400">
                  {t(`vocab.scenarioClass.${m.member_class}`, {
                    defaultValue: m.member_class,
                  })}
                </span>
                {m.offset_hours > 0 && (
                  <span className="ms-1 text-slate-500">
                    {t("screens.scenariosView.members.offset", {
                      n: m.offset_hours,
                    })}
                  </span>
                )}
              </span>
              {isAdmin && (
                <button
                  type="button"
                  data-testid="scenario-member-remove"
                  onClick={() =>
                    remove.mutate(m.member_scenario_id, {
                      // ROUND 15 D12 -- "returned to the library" only
                      // when it has; otherwise where it still sits.
                      onSuccess: (left) =>
                        toast.push({
                          tone: "success",
                          message:
                            (left?.still_member_of?.length ?? 0) > 0
                              ? t("screens.scenariosView.members.removedStill", {
                                  names: left.still_member_of
                                    .map((c) => c.name)
                                    .join(", "),
                                })
                              : t("screens.scenariosView.members.removed"),
                        }),
                    })
                  }
                  className="text-[11px] text-rose-700 hover:underline"
                >
                  {t("screens.scenariosView.members.remove")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {isAdmin && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select
            aria-label={t("screens.scenariosView.members.pick")}
            data-testid="scenario-member-pick"
            value={memberId}
            onChange={(e) => setMemberId(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1 text-xs"
          >
            <option value="">{t("screens.scenariosView.members.pick")}</option>
            {candidates.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <input
            aria-label={t("screens.scenariosView.members.offsetLabel")}
            data-testid="scenario-member-offset"
            type="number"
            min={0}
            value={offset}
            onChange={(e) => setOffset(e.target.value)}
            className="w-24 rounded border border-slate-300 px-2 py-1 text-xs"
          />
          <button
            type="button"
            data-testid="scenario-member-add"
            disabled={!memberId || add.isPending}
            onClick={() =>
              add.mutate(
                {
                  member_scenario_id: memberId,
                  offset_hours: Number(offset) || 0,
                },
                {
                  onSuccess: () => {
                    setMemberId("");
                    setOffset("0");
                    toast.push({
                      tone: "success",
                      message: t("screens.scenariosView.members.added", {
                        composite:
                          scenarios.find((s) => s.id === scenarioId)?.name ??
                          t("screens.scenariosView.members.heading"),
                      }),
                    });
                  },
                  onError: (e) =>
                    toast.push({
                      tone: "error",
                      message: t("screens.scenariosView.members.addFailed"),
                      detail: e.detail,
                    }),
                },
              )
            }
            className="rounded bg-brand-brass px-2 py-1 text-xs font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
          >
            {t("screens.scenariosView.members.add")}
          </button>
        </div>
      )}
      <p className="mt-2 text-[11px] text-slate-500">
        {t("screens.scenariosView.members.note")}
      </p>
    </section>
  );
}
